// =============================================================================
// utils/status.js
//
// Fonte única do "display status" de uma atividade, partilhada pela tabela
// (TrackerTable) e pela filtragem (TrackerPage). Antes esta lógica vivia só
// no TrackerTable; ao passar a existir também um filtro de Status na barra,
// as duas têm de calcular exatamente o mesmo, por isso ficou centralizada
// aqui.
//
// Estados (por especificação — não há mais nenhum definido no tracker):
//   Closed      → status = 'Closed' guardado explicitamente (independente das datas)
//   Overdue     → passaram mais de 2 semanas desde a EndDate e nunca foi fechada
//   Planned     → StartDate ainda está no futuro (não começou)
//   In Progress → hoje está entre Start e End, OU já passou a End mas ainda
//                 dentro das 2 semanas de tolerância, OU já começou sem End
//
// Sem data de fim (atividade em aberto) nunca fica Overdue.
//
// Regra do Overdue (item #7): antes era "hoje > EndDate"; passou a ser
// "hoje > EndDate + 2 semanas" — há uma tolerância de 14 dias após o fim
// antes de a atividade ser marcada como Overdue.
// =============================================================================

// Ordem canónica dos estados — usada para popular o filtro Status.
export const STATUS_VALUES = ['In Progress', 'Planned', 'Overdue', 'Closed'];

// Tolerância após a End Date antes de marcar Overdue (2 semanas).
const OVERDUE_GRACE_MS = 14 * 24 * 60 * 60 * 1000;

// Converte string de data da API (vários formatos) → timestamp UTC, ou null.
// Mesma desambiguação usada no resto do módulo (MM/dd/yyyy, ISO, yyyy-dd-MM).
const parseMs = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();

  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T00:00:00Z`).getTime();

  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
    return new Date(`${iso}T00:00:00Z`).getTime();
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.getTime();
};

export const getDisplayStatus = (entry) => {
  if ((entry.status ?? 'Open') === 'Closed') return 'Closed';

  const now   = Date.now();
  const start = parseMs(entry.startDate);
  const end   = parseMs(entry.endDate);

  if (end != null && now > end + OVERDUE_GRACE_MS) return 'Overdue';
  if (start != null && start > now) return 'Planned';
  return 'In Progress';
};
